import { NAV_BY_APP } from "@/lib/appNavigation";

/** Extra hub routes not listed in primary nav bars (deep links, create flows). */
const EXTRA_BREADCRUMB_LABELS: Readonly<Record<string, string>> = {
  "/manufacturing-management/edit-batch": "Edit batch",
  "/mrp/tla-planning/detail": "TLA planning detail",
  "/mrp/purchase-orders/compose": "New purchase order",
  "/mrp/rfqs/compose": "New RFQ",
  "/mrp/purchase-invoices": "Purchase invoices",
  "/bom/test-revision": "BoM test revision",
  "/manufacturing/final-qc/mistaken-pass": "Mistaken pass",
  "/clamp-qc/reporting/work-orders": "Work order lines",
  "/shipping/inbound/receive": "Receive",
  "/shipping/receive/discrepancies": "Holding",
  "/shipping/pre-pack": "Pre-pack queue",
  "/pre-pack/carton": "Carton",
  "/inside-sales/sales-orders/new": "Create sales order",
  "/accounting/cash-flow": "Cash flow forecast",
  "/accounting/ap/new": "Add vendor invoice",
  "/personal/safety-reports/new": "New safety report",
};

/** Dynamic hub routes keyed by pathname pattern (used when no exact nav match). */
const DYNAMIC_BREADCRUMB_PATTERNS: ReadonlyArray<{ test: RegExp; label: string }> = [
  { test: /^\/accounting\/ap\/[^/]+\/edit$/u, label: "Edit vendor invoice" },
  { test: /^\/accounting\/suntronic-invoices\/[^/]+$/u, label: "Manufacturer invoice" },
  { test: /^\/manufacturing-management\/work-orders\/\d+$/u, label: "Work order detail" },
  { test: /^\/mrp\/purchase-invoices\/\d+$/u, label: "Purchase invoices" },
  { test: /^\/mrp\/adjustments\/\d+$/u, label: "Adjustment" },
  { test: /^\/mrp\/rfqs\/\d+$/u, label: "RFQ detail" },
  { test: /^\/manufacturing-management\/assembly-work-instructions$/u, label: "Assembly work instructions" },
  { test: /^\/manufacturing\/assembly\/work-orders\/\d+$/u, label: "Assembly work order" },
  { test: /^\/commissions\/runs\/[^/]+$/u, label: "Commission run" },
  { test: /^\/personal\/tickets\/\d+$/u, label: "Support ticket" },
  { test: /^\/personal\/safety-reports\/\d+$/u, label: "Safety report" },
  { test: /^\/engineering\/firmware\/\d+$/u, label: "Firmware file" },
  { test: /^\/personal\/purchase-requests\/\d+$/u, label: "Purchase request" },
  { test: /^\/quality\/qc-analysis\/\d+$/u, label: "RMA" },
  { test: /^\/quality\/cases\/\d+$/u, label: "Quality case" },
  { test: /^\/quality\/events\/\d+$/u, label: "Quality event" },
  { test: /^\/quality\/ncrs\/\d+$/u, label: "NCR" },
  { test: /^\/quality\/corrective-actions\/\d+$/u, label: "Corrective action" },
  { test: /^\/inside-sales\/sales-orders\/\d+$/u, label: "Sales order" },
  { test: /^\/quality\/cases\/\d+(?:\/investigations\/\d+)?\/traveler$/u, label: "Investigation traveler" },
  { test: /^\/clamp-qc\/receive\/[^/]+$/u, label: "Receive" },
];

/** Deep routes whose parent is not a simple nav-prefix match (refresh inference). */
const BREADCRUMB_PARENT_OVERRIDES: ReadonlyArray<{
  test: RegExp;
  parents: ReadonlyArray<{ pathname: string; label: string }>;
}> = [
  {
    test: /^\/manufacturing-management\/edit-batch/u,
    parents: [{ pathname: "/manufacturing-management/incoming-orders", label: "Incoming orders" }],
  },
  {
    test: /^\/accounting\/ap\/[^/]+\/edit$/u,
    parents: [{ pathname: "/accounting/ap", label: "Vendor invoices" }],
  },
  {
    test: /^\/accounting\/suntronic-invoices\/[^/]+$/u,
    parents: [{ pathname: "/accounting/suntronic-invoices", label: "Manufacturer invoices" }],
  },
  {
    test: /^\/bom\/test-revision/u,
    parents: [{ pathname: "/bom/set-bom-rules", label: "Set BoM rules" }],
  },
  {
    test: /^\/manufacturing\/final-qc\/mistaken-pass/u,
    parents: [{ pathname: "/manufacturing/final-qc", label: "Final QC" }],
  },
  {
    test: /^\/quality\/cases\/\d+(?:\/investigations\/\d+)?\/traveler$/u,
    parents: [{ pathname: "/quality/cases", label: "Quality cases" }],
  },
  {
    test: /^\/quality\/ncrs\/\d+$/u,
    parents: [{ pathname: "/quality/events", label: "Events" }],
  },
  {
    test: /^\/engineering\/firmware\/\d+$/u,
    parents: [{ pathname: "/engineering/firmware", label: "Firmware" }],
  },
];

const NAV_LABELS: Readonly<Record<string, string>> = (() => {
  const out: Record<string, string> = { ...EXTRA_BREADCRUMB_LABELS };
  for (const items of Object.values(NAV_BY_APP)) {
    for (const item of items) {
      out[item.to] = item.label;
    }
  }
  return out;
})();

const MODULE_HOME_LABELS: Readonly<Record<string, string>> = {
  "/erp": "Megaventory ingest",
  "/mrp": "Material requirements planning",
  "/bom": "BoM tools",
  "/admin": "Administration",
  "/clamp-qc": "Clamp inspection",
  "/manufacturing": "Manufacturing",
  "/manufacturing-management": "Manufacturing management",
  "/documents": "Document control",
  "/wiki": "Wiki",
  "/shipping": "Shipping",
  "/accounting": "Accounting",
  "/quality": "Quality",
  "/inside-sales": "Inside Sales",
  "/art-and-labels": "Art and Labels",
  "/printing": "Printing",
};

function humanizePathSegment(segment: string): string {
  const decoded = decodeURIComponent(segment);
  if (decoded.length === 0) return decoded;
  return decoded
    .split(/[-_]/u)
    .map((part) => (part.length > 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(" ");
}

/**
 * Best-effort label for a pathname when the page has not called `usePageBreadcrumb`.
 * Prefers exact nav entries, then module homes, then the last path segment.
 */
export function staticBreadcrumbLabelForPath(pathname: string): string {
  const normalized = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const exact = NAV_LABELS[normalized] ?? MODULE_HOME_LABELS[normalized];
  if (exact != null) return exact;

  for (const pattern of DYNAMIC_BREADCRUMB_PATTERNS) {
    if (pattern.test.test(normalized)) return pattern.label;
  }

  const segments = normalized.split("/").filter((segment) => segment.length > 0);
  if (segments.length === 0) return "Home";
  return humanizePathSegment(segments[segments.length - 1] ?? "");
}

/** Longest nav list route that may parent a deeper hub page (for trail inference on refresh). */
export function listNavBreadcrumbParentCandidates(): Array<{ pathname: string; label: string }> {
  const candidates = new Map<string, string>();
  for (const [pathname, label] of Object.entries(NAV_LABELS)) {
    candidates.set(pathname, label);
  }
  return [...candidates.entries()]
    .map(([pathname, label]) => ({ pathname, label }))
    .sort((left, right) => right.pathname.length - left.pathname.length);
}

export function listBreadcrumbParentOverrides(): ReadonlyArray<{
  test: RegExp;
  parents: ReadonlyArray<{ pathname: string; label: string }>;
}> {
  return BREADCRUMB_PARENT_OVERRIDES;
}

export function listModuleHomeNavigationPaths(): string[] {
  return Object.keys(MODULE_HOME_LABELS);
}

export function isModuleHomeNavigationPath(pathname: string): boolean {
  const normalized = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return MODULE_HOME_LABELS[normalized] != null;
}
